import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CLIENT_LOCALE } from '@/lib/client-locale'
import {
  filterCountryOptions,
  getCountryLabel,
  type CountryOption,
} from '@/lib/country-codes'
import { cn } from '@/lib/utils'

type CountrySelectProps = {
  id?: string
  value: string
  onValueChange: (code: string) => void
  disabled?: boolean
  placeholder?: string
  className?: string
}

export function CountrySelect({
  id,
  value,
  onValueChange,
  disabled,
  placeholder = 'Select country',
  className,
}: CountrySelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const normalized = value.trim().toUpperCase()
  const selectedLabel = normalized ? getCountryLabel(normalized, CLIENT_LOCALE) : ''

  const options = useMemo(() => filterCountryOptions(query, CLIENT_LOCALE), [query])

  const pick = (option: CountryOption) => {
    onValueChange(option.code)
    setOpen(false)
    setQuery('')
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setQuery('')
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-9 w-full justify-between font-normal',
            !selectedLabel && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2" align="start">
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search countries…"
          className="mb-2 h-8"
          autoComplete="off"
          autoFocus
        />
        <div className="max-h-60 overflow-y-auto rounded-md border border-border/60">
          {options.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">No countries found</p>
          ) : (
            <ul className="p-1" role="listbox">
              {options.map((option) => {
                const isSelected = option.code === normalized
                return (
                  <li key={option.code}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                        isSelected && 'bg-accent/60',
                      )}
                      onClick={() => pick(option)}
                    >
                      <Check
                        className={cn('h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                        aria-hidden
                      />
                      <span className="truncate">
                        {option.label}
                        <span className="ml-1 text-muted-foreground">({option.code})</span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
